import { useEffect, useState } from "react";

const POST_MINUTE_OFFSET_MS = 250;

const msUntilNextTick = (): number => {
    const msSinceMinute = Date.now() % 60_000;
    return 60_000 - msSinceMinute + POST_MINUTE_OFFSET_MS;
};

/**
 * Re-renders the caller at each wall-clock minute boundary (12:01:00, 12:02:00, …),
 * plus a small offset to absorb scheduler jitter. Use when UI depends on whole-minute
 * timestamps. Pauses while the document is hidden.
 *
 * Returns `Date.now()` at each tick; callers wanting only the re-render can ignore it.
 */
export const useNow = (): number => {
    const [now, setNow] = useState(() => Date.now());

    useEffect(() => {
        let timeoutId: ReturnType<typeof setTimeout> | undefined;

        const scheduleNext = () => {
            timeoutId = setTimeout(() => {
                setNow(Date.now());
                scheduleNext();
            }, msUntilNextTick());
        };

        const handleVisibilityChange = () => {
            clearTimeout(timeoutId);
            if (document.visibilityState === "visible") {
                setNow(Date.now());
                scheduleNext();
            }
        };

        // useState initializer already captured Date.now(); just schedule the ticker without a redundant setNow.
        if (document.visibilityState === "visible") scheduleNext();
        document.addEventListener("visibilitychange", handleVisibilityChange);

        return () => {
            clearTimeout(timeoutId);
            document.removeEventListener("visibilitychange", handleVisibilityChange);
        };
    }, []);

    return now;
};
